import React, { useEffect, useId, useRef, useState } from "react";

const GLYPHS = {
  "0": "M3 16 C3 6 7 2 12 2 C17 2 21 6 21 16 C21 26 17 30 12 30 C7 30 3 26 3 16",
  "1": "M7 8 L12 3 L12 29 M8 29 L17 29",
  "2": "M4 9 C4 5 7 2 12 2 C17 2 20 5 20 9 C20 14 15 17 5 26 L4 29 L21 29",
  "3": "M4 5 C8 1 18 1 20 6 C21 10 17 13 12 14 C18 14 21 17 20 23 C19 29 8 32 4 27",
  "4": "M17 29 L17 3 L4 20 L22 20",
  "5": "M20 3 L6 3 L5 14 C9 11 18 12 20 18 C23 27 15 31 8 29 C5 28 3 27 2 25",
  "6": "M19 4 C15 1 8 3 5 10 C1 20 5 29 12 30 C18 31 21 26 20 21 C19 15 12 13 5 18",
  "7": "M3 3 L21 3 L8 29",
  "8": "M12 16 C5 16 3 13 4 8 C5 2 19 1 20 8 C21 13 18 16 12 16 C5 16 3 20 4 25 C5 31 19 31 20 25 C21 20 18 16 12 16",
  "9": "M19 18 C13 22 5 19 4 12 C3 5 8 2 13 2 C20 2 22 10 19 18 C17 25 13 28 7 29",
  "$": "M16 5 C13 2 6 3 5 8 C4 13 9 14 14 16 C19 18 20 23 17 27 C14 31 7 30 4 27 M11 1 L11 31",
  ".": "M12 26 C14 26 15 27 15 29 C15 31 14 32 12 32 C10 32 9 31 9 29 C9 27 10 26 12 26",
  ",": "M12 27 C12 29 10 31 8 32",
};

const GLYPH_STROKES = {
  "$": [
    "M16 5 C13 2 6 3 5 8 C4 13 9 14 14 16 C19 18 20 23 17 27 C14 31 7 30 4 27",
    "M11 1 L11 31",
  ],
};

export const MOVEMENT_PAUSE_MS = 80;
export const MIN_GLYPH_DURATION_MS = 300;
export const MAX_GLYPH_DURATION_MS = 550;
export const ENGRAVING_UNITS_PER_MS = 0.18;
export const PUNCTUATION_DURATION_MS = 180;
export const LASER_LEAD_DISTANCE = 3;
export const FINAL_CATCH_UP_MS = 65;
const SMOKE_WISP_COUNT = 10;
const SMOKE_SPAWN_INTERVAL_MS = 55;
const SMOKE_TONES = ["rgb(47, 39, 33)", "rgb(82, 68, 57)", "rgb(125, 108, 92)"];

export function glyphDuration(pathLength, character) {
  if (character === "." || character === ",") return PUNCTUATION_DURATION_MS;
  return Math.max(MIN_GLYPH_DURATION_MS, Math.min(MAX_GLYPH_DURATION_MS, pathLength / ENGRAVING_UNITS_PER_MS));
}

export function buildSequentialTimeline(lengths, pause = MOVEMENT_PAUSE_MS, characters = []) {
  let cursor = 0;
  return lengths.map((length, index) => {
    const duration = glyphDuration(length, characters[index]);
    const item = { start: cursor, duration, end: cursor + duration };
    cursor = item.end + pause;
    return item;
  });
}

export function strokeDashOffset(totalLength, revealedLength) {
  if (!totalLength) return 100;
  return Math.max(0, 100 * (1 - (revealedLength / totalLength)));
}

export function glyphStrokes(character) {
  return GLYPH_STROKES[character] || (GLYPHS[character] ? [GLYPHS[character]] : []);
}

function formatMinor(amountMinor, currency = "USD") {
  const amount = Number(amountMinor);
  if (!Number.isSafeInteger(amount)) return "";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount / 100);
}

function glyphEntries(text, animate = true) {
  return String(text).split("").map((character) => ({ character, animate: animate && Boolean(GLYPHS[character]) }));
}

export function engravingCharacters(text) {
  return glyphEntries(text).filter((entry) => entry.animate).map((entry) => entry.character);
}

export default function LaserEtchedPrice({ amountMinor, currency = "USD", saleAmountMinor, replayKey, observe = false, segments, className = "", style }) {
  const svgRef = useRef(null);
  const laserRef = useRef(null);
  const beamRef = useRef(null);
  const smokeWispRefs = useRef([]);
  const smokeParticlesRef = useRef([]);
  const smokeFrameRef = useRef(null);
  const smokeCursorRef = useRef(0);
  const frameRef = useRef(null);
  const animationRunRef = useRef(0);
  const idPrefix = useId().replace(/:/g, "");
  const scorchFilterId = `${idPrefix}-scorch`;
  const smokeFilterId = `${idPrefix}-smoke`;
  const [entered, setEntered] = useState(!observe);
  const regularText = formatMinor(amountMinor, currency);
  const saleText = saleAmountMinor != null ? formatMinor(saleAmountMinor, currency) : null;
  const hasSale = saleText && Number(saleAmountMinor) < Number(amountMinor);
  const activeSegments = segments || (hasSale ? [{ text: regularText, animate: false }, { text: saleText, animate: true }] : [{ text: regularText, animate: true }]);
  const entries = activeSegments.flatMap((segment) => glyphEntries(segment.text, segment.animate !== false));
  const accessibleText = segments ? activeSegments.map((segment) => segment.text).join("") : hasSale ? `${regularText}, sale price ${saleText}` : regularText;
  const animationKey = replayKey ?? accessibleText;
  const width = Math.max(24, entries.length * 18);

  useEffect(() => {
    if (!observe) return undefined;
    const node = svgRef.current;
    const card = node?.closest("article");
    if (!card) return undefined;

    // On touch devices there's no hover, so the engraving would never run.
    // Trigger it once when the card scrolls into view instead.
    const isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
    if (isTouch) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setEntered(true); // run once; do not reset on scroll-out
            io.disconnect();
          }
        },
        { threshold: 0.4 },
      );
      io.observe(card);
      return () => io.disconnect();
    }

    let frame;
    const start = () => {
      setEntered(false);
      frame = window.requestAnimationFrame(() => setEntered(true));
    };
    const reset = (event) => {
      if (!event.relatedTarget || !card.contains(event.relatedTarget)) setEntered(false);
    };
    card.addEventListener("mouseenter", start);
    card.addEventListener("mouseleave", reset);
    card.addEventListener("focusin", start);
    card.addEventListener("focusout", reset);
    return () => {
      card.removeEventListener("mouseenter", start);
      card.removeEventListener("mouseleave", reset);
      card.removeEventListener("focusin", start);
      card.removeEventListener("focusout", reset);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [observe]);

  useEffect(() => {
    if (!entered || !svgRef.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const runId = animationRunRef.current + 1;
    animationRunRef.current = runId;
    let cancelled = false;
    const activeLaser = laserRef.current;
    const activeBeam = beamRef.current;
    const pause = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    const toRootPoint = (localPoint, pathScreenCTM, rootScreenInverse) => {
      const screenPoint = new DOMPoint(localPoint.x, localPoint.y).matrixTransform(pathScreenCTM);
      return screenPoint.matrixTransform(rootScreenInverse);
    };
    const leadDistanceInPathUnits = (path, pathScreenCTM, laserDistance) => {
      if (laserDistance <= 0) return 0;
      const sampleLength = Math.min(1, laserDistance);
      const point = path.getPointAtLength(laserDistance);
      const previousPoint = path.getPointAtLength(laserDistance - sampleLength);
      const screenPoint = new DOMPoint(point.x, point.y).matrixTransform(pathScreenCTM);
      const previousScreenPoint = new DOMPoint(previousPoint.x, previousPoint.y).matrixTransform(pathScreenCTM);
      const screenUnitsPerPathUnit = Math.hypot(screenPoint.x - previousScreenPoint.x, screenPoint.y - previousScreenPoint.y) / sampleLength;
      return Math.min(laserDistance, LASER_LEAD_DISTANCE / Math.max(screenUnitsPerPathUnit, 0.001));
    };
    const clearSmoke = () => {
      smokeParticlesRef.current = [];
      smokeWispRefs.current.forEach((wisp) => { if (wisp) wisp.style.opacity = "0"; });
    };
    const renderSmoke = (timestamp) => {
      let hasActiveSmoke = false;
      smokeParticlesRef.current.forEach((particle, index) => {
        const wisp = smokeWispRefs.current[index];
        if (!particle || !wisp) return;
        const progress = Math.min(1, (timestamp - particle.birthTime) / particle.lifetime);
        if (progress >= 1) {
          wisp.style.opacity = "0";
          smokeParticlesRef.current[index] = null;
          return;
        }
        hasActiveSmoke = true;
        wisp.setAttribute("cx", particle.originX + (particle.horizontalDrift * progress));
        wisp.setAttribute("cy", particle.originY - (particle.riseDistance * progress));
        wisp.setAttribute("r", particle.startingRadius + ((particle.endingRadius - particle.startingRadius) * progress));
        wisp.style.opacity = String((1 - progress) * particle.opacity);
      });
      return hasActiveSmoke;
    };
    const keepSmokeAlive = (timestamp) => {
      if (cancelled || !renderSmoke(timestamp)) {
        smokeFrameRef.current = null;
        return;
      }
      smokeFrameRef.current = window.requestAnimationFrame(keepSmokeAlive);
    };
    const spawnSmoke = (origin, timestamp) => {
      const index = smokeCursorRef.current % SMOKE_WISP_COUNT;
      smokeCursorRef.current += 1;
      smokeParticlesRef.current[index] = {
        originX: origin.x,
        originY: origin.y,
        birthTime: timestamp,
        lifetime: 700 + ((index % 4) * 75),
        horizontalDrift: ((index % 3) - 1) * 1.6,
        riseDistance: 8 + ((index % 4) * 1.5),
        startingRadius: 2.6,
        endingRadius: 5.8 + (index % 3),
        opacity: 0.4 - ((index % 3) * 0.05),
      };
      if (!smokeFrameRef.current) smokeFrameRef.current = window.requestAnimationFrame(keepSmokeAlive);
    };
    const run = async () => {
      if (cancelled || animationRunRef.current !== runId || !svgRef.current || !activeLaser || !activeBeam) return;
      const glyphs = [...svgRef.current.querySelectorAll("[data-engrave='true']")];
      const laser = activeLaser;
      for (const glyph of glyphs) {
        if (cancelled || animationRunRef.current !== runId) return;
        const strokes = [...glyph.querySelectorAll("[data-engraving-stroke]")];
        for (const stroke of strokes) {
          const motionPath = stroke.querySelector(".laser-motion-path");
          if (cancelled || !svgRef.current || !motionPath || !motionPath.isConnected) return;
          const engravingLayers = [...stroke.querySelectorAll(".laser-scorch-halo, .laser-charred-groove, .laser-engraved-center")];
          const length = motionPath.getTotalLength();
          const duration = Math.max(1, length / ENGRAVING_UNITS_PER_MS);
          const pathScreenCTM = motionPath.getScreenCTM();
          const rootScreenCTM = svgRef.current.getScreenCTM();
          if (!pathScreenCTM || !rootScreenCTM) return;
          const rootScreenInverse = rootScreenCTM.inverse();
          engravingLayers.forEach((layer) => { layer.style.strokeDashoffset = "100"; });
          const completed = await new Promise((resolve) => {
            let startTime;
            let catchUpStartTime;
            let lastSmokeTime = -SMOKE_SPAWN_INTERVAL_MS;
            const drawFrame = (timestamp) => {
              if (cancelled || animationRunRef.current !== runId || !svgRef.current || !motionPath.isConnected || !laserRef.current || !beamRef.current) return resolve(false);
              startTime ??= timestamp;
              const elapsed = timestamp - startTime;
              const laserDistance = Math.min(length, ENGRAVING_UNITS_PER_MS * elapsed);
              const leadDistance = leadDistanceInPathUnits(motionPath, pathScreenCTM, laserDistance);
              const initialBurnDistance = Math.max(0, laserDistance - leadDistance);
              catchUpStartTime ??= laserDistance === length ? timestamp : undefined;
              const catchUpProgress = catchUpStartTime == null ? 0 : Math.min(1, (timestamp - catchUpStartTime) / FINAL_CATCH_UP_MS);
              const burnDistance = catchUpStartTime == null ? initialBurnDistance : initialBurnDistance + ((length - initialBurnDistance) * catchUpProgress);
              const laserPoint = toRootPoint(motionPath.getPointAtLength(laserDistance), pathScreenCTM, rootScreenInverse);
              const burnPoint = toRootPoint(motionPath.getPointAtLength(burnDistance), pathScreenCTM, rootScreenInverse);
              const dashOffset = strokeDashOffset(length, burnDistance);

              laser.setAttribute("cx", laserPoint.x);
              laser.setAttribute("cy", laserPoint.y);
              laser.style.opacity = "1";
              activeBeam.setAttribute("x1", burnPoint.x);
              activeBeam.setAttribute("y1", burnPoint.y);
              activeBeam.setAttribute("x2", laserPoint.x);
              activeBeam.setAttribute("y2", laserPoint.y);
              activeBeam.style.opacity = "1";
              engravingLayers.forEach((layer) => { layer.style.strokeDashoffset = String(dashOffset); });
              renderSmoke(timestamp);
              if (burnDistance > 0 && timestamp - lastSmokeTime >= SMOKE_SPAWN_INTERVAL_MS) {
                spawnSmoke(burnPoint, timestamp);
                lastSmokeTime = timestamp;
              }

              if (catchUpProgress < 1) {
                frameRef.current = window.requestAnimationFrame(drawFrame);
                return;
              }
              resolve(true);
            };
            frameRef.current = window.requestAnimationFrame(drawFrame);
          });
          if (!completed || cancelled) return;
          laser.style.opacity = "0";
          activeBeam.style.opacity = "0";
          await pause(MOVEMENT_PAUSE_MS);
        }
        await pause(MOVEMENT_PAUSE_MS);
      }
    };
    run();
    return () => {
      cancelled = true;
      animationRunRef.current += 1;
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      if (smokeFrameRef.current) window.cancelAnimationFrame(smokeFrameRef.current);
      smokeFrameRef.current = null;
      clearSmoke();
      if (activeLaser) activeLaser.style.opacity = "0";
      if (activeBeam) activeBeam.style.opacity = "0";
    };
  }, [animationKey, entered]);

  return (
    <div className={`laser-etched-price ${className}`} style={{ ...style, visibility: observe && !entered ? "hidden" : "visible" }}>
      <span className="sr-only">{accessibleText}</span>
      <svg ref={svgRef} key={animationKey} aria-hidden="true" className="laser-etched-svg-sequence" viewBox={`0 0 ${width} 34`} preserveAspectRatio="xMinYMid meet">
        <defs>
          <filter id={scorchFilterId} x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="2.25" /></filter>
          <filter id={smokeFilterId} x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="0.8" /></filter>
        </defs>
        {entries.map((entry, index) => {
          const x = index * 18;
          const strokes = glyphStrokes(entry.character);
          if (!strokes.length) return null;
          const completeClass = entry.animate ? "" : "laser-path-complete";
          return <g key={`${idPrefix}-${index}-${entry.character}`} transform={`translate(${x} 0)`} data-engrave={entry.animate ? "true" : "false"} data-character={entry.character}>{strokes.map((path, strokeIndex) => <g key={strokeIndex} data-engraving-stroke={strokeIndex}><path d={path} pathLength="100" className="laser-guide-path" /><path d={path} pathLength="100" vectorEffect="non-scaling-stroke" filter={`url(#${scorchFilterId})`} className={`laser-scorch-halo ${completeClass}`} /><path d={path} pathLength="100" vectorEffect="non-scaling-stroke" className={`laser-charred-groove ${completeClass}`} /><path d={path} pathLength="100" vectorEffect="non-scaling-stroke" className={`laser-engraved-center ${completeClass}`} /><path d={path} className="laser-motion-path" /></g>)}</g>;
        })}
        <g className="laser-smoke-layer" filter={`url(#${smokeFilterId})`}>
          {Array.from({ length: SMOKE_WISP_COUNT }, (_, index) => <circle key={index} ref={(node) => { smokeWispRefs.current[index] = node; }} className="laser-smoke-wisp" fill={SMOKE_TONES[index % SMOKE_TONES.length]} />)}
        </g>
        <line ref={beamRef} className="laser-shared-beam" x1="0" y1="0" x2="0" y2="0" />
        <circle ref={laserRef} className="laser-shared-point" r="1.5" cx="0" cy="0" />
      </svg>
    </div>
  );
}
